import React from 'react';
import { useMentionCount } from '@/components/mentions/useMentionCount';

import {
	ChevronRight,
  AtSign,
	Hash,
	MessagesSquare,
	Star,
	UsersRound,
} from 'lucide-react';

import { useLocation, Link } from 'react-router-dom';

import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
	SidebarGroup,
	SidebarGroupLabel,
	SidebarMenu,
	SidebarMenuAction,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
} from '@/components/ui/sidebar';
import { useDispatch, useSelector } from 'react-redux';
import { TeamConversation, User } from '@/types';
import {
	markConversationAsRead,
	setActiveTeamConversation,
} from '@/actions/teams';
import { cn } from '@/lib/utils';
import UserAvatar from '../webchat/teams/UserAvatar';

type TeamConversationWithUrlAndAvatar = TeamConversation & { url: string } & {
	avatar?: any;
};

const getTeamsNavItems = ({
	channels,
	starredChannels,
	dms,
	groupChats,
	starredGroupChats,
	starredDms,
	unreadMessagesForChannels,
	unreadMessagesForGroups,
	unreadMessagesForDms,
}: {
	channels: TeamConversationWithUrlAndAvatar[];
	starredChannels: TeamConversationWithUrlAndAvatar[];
	dms: TeamConversationWithUrlAndAvatar[];
	groupChats: TeamConversationWithUrlAndAvatar[];
	starredGroupChats: TeamConversationWithUrlAndAvatar[];
	starredDms: TeamConversationWithUrlAndAvatar[];
	unreadMessagesForChannels: boolean;
	unreadMessagesForGroups: boolean;
	unreadMessagesForDms: boolean;
}) => {
	return [
		// Channels are made for the location and its departments, so the
		// section only shows once there is one
		...(channels.length || starredChannels.length
			? [
					{
						title: 'Channels',
						url: '/groupchat/groups',
						icon: Hash,
						items: channels,
						starredChats: starredChannels,
						isTeams: true,
						isUnread: unreadMessagesForChannels,
						isChannels: true,
					},
				]
			: []),
		{
			title: 'Groups',
			url: '/groupchat/groups',
			icon: UsersRound,
			items: groupChats,
			starredChats: starredGroupChats,
			isTeams: true,
			isUnread: unreadMessagesForGroups,
		},
		{
			title: 'Direct Messages',
			url: '/groupchat/dms',
			icon: MessagesSquare,
			items: dms,
			starredChats: starredDms,
			isTeams: true,
			showAvatar: true,
			isUnread: unreadMessagesForDms,
		},
	];
};

const getDMTitle = (tc: TeamConversation, authUser: User, users: User[]) => {
	const userFilteredFromConversation = tc._users.find(
		(u) => u !== authUser._id,
	);
	const userFromDirectMessage = users.find(
		(u: User) => u._id === userFilteredFromConversation,
	);
	return userFromDirectMessage ? userFromDirectMessage.name : tc.title;
};

const getDMAvatar = (tc: TeamConversation, authUser: User, users: User[]) => {
	const userFilteredFromConversation = tc._users.find(
		(u) => u !== authUser._id,
	);
	const userFromDirectMessage = users.find(
		(u: User) => u._id === userFilteredFromConversation,
	);
	return (
		<UserAvatar
			user={userFromDirectMessage}
			showName={false}
			showNameBelow={false}
			avatarHeight="20px"
			avatarWidth="20px"
			avatarBgColor={false}
			avatarFontSize="10px"
			marginLeft={0}
			marginTop={0}
			nameFontSize={0}
			onlineIndicatorHeight="0.65rem"
			onlineIndicatorWidth="0.65rem"
		/>
	);
};

const getCompleteTeamConversations = ({
	teamConversations,
	type,
	authUser,
	users,
	shouldContainStarredMessages,
}: {
	teamConversations: TeamConversation[];
	type: string;
	authUser: User;
	users: User[];
	shouldContainStarredMessages: boolean;
}) => {
	return teamConversations
		.map((tc) => ({
			...tc,
			title: type === 'dms' ? getDMTitle(tc, authUser, users) : tc.title,
			url: `/groupchat/${type}/conversation/groups/${
				tc._id
			}?_teamMessage=${tc?.teamMessages?.[0]._id ?? ''}&_location=${
				tc._business
			}&isDirectMessage=${tc.isDirectMessage}`,
			avatar: type === 'dms' && getDMAvatar(tc, authUser, users),
		}))
		.filter((tc) => {
			if (shouldContainStarredMessages) {
				return tc._starredUsers?.includes(authUser._id);
			}
			return !tc._starredUsers?.includes(authUser._id);
		});
};

const hasUnreadMessages = (
	teamConversations: TeamConversationWithUrlAndAvatar[] | TeamConversation[],
	_user: string,
) => {
	return (
		teamConversations.filter((tc) => tc._unreadUsers.includes(_user))
			.length > 0
	);
};

const doesConversationHaveUnreadMessages = ({
	tc,
	_user,
}: {
	tc: TeamConversationWithUrlAndAvatar | TeamConversation;
	_user: string;
}) => {
	if (!tc._id) return false;
	return tc._unreadUsers.includes(_user);
};

const isSubItemActive = (
	subItem: TeamConversationWithUrlAndAvatar | Record<string, unknown>,
	activeTeamConversation: string,
	isTeams: boolean | undefined,
	locationPathname: string,
) => {
	return isTeams
		? subItem._id === activeTeamConversation
		: locationPathname === subItem.url;
};

export function NavRunner({
	openStates,
	handleToggle,
}: {
	openStates: Record<string, boolean>;
	handleToggle: (title: string, isOpen: boolean) => void;
}) {
	const location = useLocation();
  const teamMentionCount = useMentionCount(true);
	const dispatch = useDispatch();

	const teams = useSelector((state: any) => state.teams);
	const auth = useSelector((state: any) => state.auth);
	const users = useSelector((state: any) => state.users);
	const teamConversations = teams.teamConversations;
	const _activeTeamConversation = teams._activeTeamConversation;

	const dms = teamConversations
		.slice()
		.filter((tc: TeamConversation) => tc.isDirectMessage)
		.sort(
			(a: TeamConversation, b: TeamConversation) =>
				new Date(b.latestMessageDate).getTime() -
				new Date(a.latestMessageDate).getTime(),
		);

	const groupChats = teamConversations
		.slice()
		.filter(
			(tc: TeamConversation) =>
				!tc.isDirectMessage && !tc?.isRunner && !tc.channel,
		)
		.sort(
			(a: TeamConversation, b: TeamConversation) =>
				new Date(b.latestMessageDate).getTime() -
				new Date(a.latestMessageDate).getTime(),
		);

	// the location's channel first, then its departments by name
	const channels = teamConversations
		.filter((tc: TeamConversation) => tc.channel)
		.sort((a: TeamConversation, b: TeamConversation) =>
			a.channel === b.channel
				? a.title.localeCompare(b.title)
				: a.channel === 'location'
					? -1
					: 1,
		);

	const unreadMessagesForChannels = React.useMemo(
		() => hasUnreadMessages(channels, auth.user._id),
		[channels, auth.user._id],
	);
	const isChannelActive = channels.some(
		(tc: TeamConversation) => tc._id === _activeTeamConversation,
	);

	const unreadMessagesForGroups = React.useMemo(
		() => hasUnreadMessages(groupChats, auth.user._id),
		[groupChats, auth.user._id],
	);

	const unreadMessagesForDms = React.useMemo(
		() => hasUnreadMessages(dms, auth.user._id),
		[dms, auth.user._id],
	);

	const teamsItems = getTeamsNavItems({
		unreadMessagesForChannels,
		channels: getCompleteTeamConversations({
			teamConversations: channels,
			type: 'groups',
			authUser: auth.user,
			users,
			shouldContainStarredMessages: false,
		}),
		starredChannels: getCompleteTeamConversations({
			teamConversations: channels,
			type: 'groups',
			authUser: auth.user,
			users,
			shouldContainStarredMessages: true,
		}),
		unreadMessagesForGroups,
		unreadMessagesForDms,
		dms: getCompleteTeamConversations({
			teamConversations: dms,
			type: 'dms',
			authUser: auth.user,
			users,
			shouldContainStarredMessages: false,
		}),
		groupChats: getCompleteTeamConversations({
			teamConversations: groupChats,
			type: 'groups',
			authUser: auth.user,
			users,
			shouldContainStarredMessages: false,
		}),
		starredGroupChats: getCompleteTeamConversations({
			teamConversations: groupChats,
			type: 'groups',
			authUser: auth.user,
			users,
			shouldContainStarredMessages: true,
		}),
		starredDms: getCompleteTeamConversations({
			teamConversations: dms,
			type: 'dms',
			authUser: auth.user,
			users,
			shouldContainStarredMessages: true,
		}),
	});

	const handleSetActiveTeamConversation = (tc: TeamConversation) => {
		dispatch(setActiveTeamConversation(tc._id) as any);
		if (doesConversationHaveUnreadMessages({ tc, _user: auth.user._id })) {
			dispatch(markConversationAsRead(tc, auth.user._id) as any);
		}
	};

	return (
		<SidebarGroup key="team">
			<SidebarGroupLabel>Team</SidebarGroupLabel>
      <SidebarMenu>
				{teamsItems.map((item) => {
					// channels and groups share the groups pages
					const isParentActive =
						location.pathname.includes(item.url) &&
						(item.title === 'Channels'
							? isChannelActive
							: item.title !== 'Groups' || !isChannelActive);
					const isChildActive = item.items?.some(
						(subItem) => location.pathname === subItem.url,
					);

					return (
						<Collapsible
							key={item.title}
							asChild
							open={openStates[item.title]}
							onOpenChange={(isOpen) =>
								handleToggle(item.title, isOpen)
							}
							defaultOpen={
								openStates[item.title] ??
								(isParentActive || isChildActive)
							}
						>
							<SidebarMenuItem>
								<SidebarMenuButton
									asChild
									tooltip={item.title}
									isActive={isParentActive}
								>
									<Link
										to={item.url}
										data-onboarding={
											item.title === 'Groups'
												? 'teams-groups'
												: item.title === 'Direct Messages'
													? 'teams-direct-messages'
													: undefined
										}
										onClick={() => {
											if (
												!openStates[item.title] &&
												item.items?.length
											) {
												handleToggle(
													item.title,
													true,
												);
											}
										}}
									>
										<item.icon className={cn('size-4')} />
										<span>{item.title}</span>
										{item.isUnread ? (
											<span className="flex h-2 w-2 rounded-full bg-primary" />
										) : null}
									</Link>
								</SidebarMenuButton>

								{item.items?.length ? (
									<>
										<CollapsibleTrigger asChild>
											<SidebarMenuAction className="data-[state=open]:rotate-90">
												<ChevronRight />
												<span className="sr-only">
													Toggle
												</span>
											</SidebarMenuAction>
										</CollapsibleTrigger>
										<CollapsibleContent>
											{item.starredChats &&
												item.starredChats.length >
													0 && (
													<SidebarMenuSub>
														{item.starredChats.map(
															(subItem) => (
																<NavItem
																	conversation={
																		subItem
																	}
																	key={
																		subItem.title
																	}
																	_activeTeamConversation={
																		_activeTeamConversation
																	}
																	isTeams={
																		item.isTeams
																	}
																	locationPathname={
																		location.pathname
																	}
																	handleSetActiveTeamConversation={
																		handleSetActiveTeamConversation
																	}
																	_user={
																		auth
																			.user
																			._id
																	}
																	showAvatar={
																		item.showAvatar
																	}
																	isStarredConversation={
																		true
																	}
																/>
															),
														)}
													</SidebarMenuSub>
												)}
											<SidebarMenuSub>
												{item.items.map(
													(subItem) => (
														<NavItem
															conversation={
																subItem
															}
															_activeTeamConversation={
																_activeTeamConversation
															}
															key={
																subItem.title
															}
															isTeams={
																item.isTeams
															}
															locationPathname={
																location.pathname
															}
															handleSetActiveTeamConversation={
																handleSetActiveTeamConversation
															}
															_user={
																auth.user
																	._id
															}
															showAvatar={
																item.showAvatar
															}
														/>
													),
												)}
											</SidebarMenuSub>
										</CollapsibleContent>
									</>
								) : null}
							</SidebarMenuItem>
						</Collapsible>
					);
				})}
        <SidebarMenuItem><SidebarMenuButton asChild isActive={location.pathname.startsWith('/team-mentions')}><Link to="/team-mentions" data-onboarding="teams-mentions"><AtSign /><span>Mentions</span>{teamMentionCount > 0 && <span className="ml-auto rounded-full bg-violet-600 px-2 text-xs text-white">{teamMentionCount > 99 ? '99+' : teamMentionCount}</span>}</Link></SidebarMenuButton></SidebarMenuItem>
			</SidebarMenu>
		</SidebarGroup>
	);
}

const NavItem = ({
	conversation,
	_activeTeamConversation,
	isTeams,
	locationPathname,
	handleSetActiveTeamConversation,
	_user,
	showAvatar,
	isStarredConversation,
}: {
	conversation:
		| TeamConversationWithUrlAndAvatar
		| { title: string; url: string; rightDecorator?: React.ReactNode };
	_activeTeamConversation: string;
	isTeams?: boolean;
	locationPathname: string;
	handleSetActiveTeamConversation: (
		tc: TeamConversationWithUrlAndAvatar,
	) => void;
	_user: string;
	showAvatar?: boolean;
	isStarredConversation?: boolean;
}) => {
	return (
		<SidebarMenuSubItem key={conversation.title}>
			<SidebarMenuSubButton
				isActive={isSubItemActive(
					conversation,
					_activeTeamConversation,
					isTeams,
					locationPathname,
				)}
				asChild
			>
				<Link
					to={conversation.url}
					onClick={() => {
						isTeams &&
							handleSetActiveTeamConversation(
								conversation as TeamConversationWithUrlAndAvatar,
							);
					}}
					key={conversation.title}
				>
					{isStarredConversation && <Star />}

					<div className="flex flex-row items-center justify-between w-full">
						<div className="flex flex-row items-center flex-1 w-full">
							<span
								className={cn(
									isTeams &&
										doesConversationHaveUnreadMessages({
											tc: conversation as TeamConversationWithUrlAndAvatar,
											_user,
										})
										? 'font-bold'
										: '',
									showAvatar
										? 'flex items-center space-x-2'
										: '',
									!isTeams &&
										'isUnread' in conversation &&
										conversation.isUnread
										? 'font-semibold flex items-center gap-2'
										: 'flex items-center',
								)}
							>
								<>
									{'avatar' in conversation &&
										conversation.avatar}
								</>
								<p className="truncate">{conversation.title}</p>
								{!isTeams &&
									'isUnread' in conversation &&
									conversation.isUnread && (
										<span className="flex h-2 w-2 rounded-full bg-primary" />
									)}
							</span>
						</div>

						<div className="flex flex-row items-center min-w-2">
							{'rightDecorator' in conversation &&
								conversation.rightDecorator &&
								conversation.rightDecorator}
						</div>
					</div>
				</Link>
			</SidebarMenuSubButton>
		</SidebarMenuSubItem>
	);
};
